import { useState, useEffect } from 'react';
import {
  buildFarmerFarmName,
  createFarmsViaDigisakaApi,
  type ExplorerFarmerOption,
} from '../../../config/digisakaApi';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import FarmerSearchPicker from './FarmerSearchPicker';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme, getModalTheme } from '../theme/themeStyles';
import {
  calculatePolygonAreaHectares,
  getPolygonInteriorLonLat,
  getPolygonCentroid,
  normalizeDrawnFieldToFeature,
  geoJsonGeometryToPolygonRings,
} from './DrawingUtils';
import {
  type DigisakaLocationCodes,
  type ProvinceRow,
  type RegionRow,
  type BlockAdminGroup,
  type BlockAdminSummary,
  adminPcodesToDigisakaCodes,
  centroidToDropdownRows,
  fetchAdminPcodesForPoints,
  fetchBarangaysListForCityCode,
  fetchCentroidLocation,
  fetchCitiesListForProvinceCode,
  fetchProvincesListForRegionCode,
  fetchRegionsListFromApi,
  analyzeBlockAdminLocations,
  resolveLocationCodesForPayload,
} from '../utils/philippinesLocationFromCentroid';
import {
  locationMatchesMonitoringArea,
  monitoringAreaSaveError,
} from '../utils/monitoringAreaLocation';
import { resolveDigisakaBarangay } from '../../../services/signupLocations';

// Province name to province code mapping for cities API
// This mapping can be extended as needed
const PROVINCE_CODE_MAP = {
  "Ilocos Norte": "0128",
  "Ilocos Sur": "0129",
  "La Union": "0133",
  "Pangasinan": "0155",
  "Batanes": "0209",
  "Cagayan": "0215",
  "Isabela": "0231",
  "Nueva Vizcaya": "0250",
  "Quirino": "0257",
  "Aurora": "0317",
  "Bataan": "0308",
  "Bulacan": "0314",
  "Nueva Ecija": "0349",
  "Pampanga": "0354",
  "Tarlac": "0369",
  "Zambales": "0371",
  "Batangas": "0410",
  "Cavite": "0421",
  "Laguna": "0434",
  "Quezon": "0456",
  "Rizal": "0458",
  "Marinduque": "1740",
  "Occidental Mindoro": "1751",
  "Oriental Mindoro": "1752",
  "Palawan": "1753",
  "Romblon": "1759",
  "Albay": "0505",
  "Camarines Norte": "0516",
  "Camarines Sur": "0517",
  "Catanduanes": "0520",
  "Masbate": "0541",
  "Sorsogon": "0562",
  "Aklan": "0604",
  "Antique": "0606",
  "Capiz": "0619",
  "Guimaras": "0679",
  "Iloilo": "0630",
  "Negros Occidental": "0645",
  "Bohol": "0712",
  "Cebu": "0722",
  "Negros Oriental": "0746",
  "Siquijor": "0761",
  "Biliran": "0878",
  "Eastern Samar": "0826",
  "Leyte": "0837",
  "Northern Samar": "0848",
  "Samar": "0860",
  "Southern Leyte": "0864",
  "Zamboanga del Norte": "0972",
  "Zamboanga del Sur": "0973",
  "Zamboanga Sibugay": "0983",
  "Bukidnon": "1013",
  "Camiguin": "1018",
  "Lanao del Norte": "1035",
  "Misamis Occidental": "1042",
  "Misamis Oriental": "1043",
  "Davao de Oro": "1123",
  "Davao del Norte": "1124",
  "Davao del Sur": "1125",
  "Davao Occidental": "1186",
  "Davao Oriental": "1126",
  "Cotabato": "1247",
  "Sarangani": "1280",
  "South Cotabato": "1263",
  "Sultan Kudarat": "1265",
  "Agusan del Norte": "1602",
  "Agusan del Sur": "1603",
  "Dinagat Islands": "1685",
  "Surigao del Norte": "1667",
  "Surigao del Sur": "1668",
  "Abra": "1401",
  "Apayao": "1481",
  "Benguet": "1411",
  "Ifugao": "1427",
  "Kalinga": "1432",
  "Mountain Province": "1444",
  "Basilan": "1507",
  "Lanao del Sur": "1536",
  "Maguindanao": "1538",
  "Sulu": "1566",
  "Tawi-Tawi": "1570",
  "Metro Manila": "1300"
};

function computeDrawnAreaHa(drawnField: any): number {
  let ha = calculatePolygonAreaHectares(drawnField);
  if (ha > 0) return ha;
  if (drawnField?.geometry) {
    ha = calculatePolygonAreaHectares(drawnField.geometry);
  }
  return ha > 0 ? ha : 0;
}

function resolveProvinceCode(provinceName: string, row: ProvinceRow | undefined): string | null {
  if (row?.province_code) return row.province_code;
  const t = provinceName.trim();
  const map = PROVINCE_CODE_MAP as Record<string, string>;
  if (map[t]) return map[t];
  const hit = Object.keys(map).find((k) => k.toLowerCase() === t.toLowerCase());
  return hit ? map[hit] : null;
}

interface SaveFieldModalProps {
  drawnField: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

/** Saves drawn field(s) via Digisaka ``POST /api/webgis/farms`` (logged-in user only). */
export default function SaveFieldModal({ drawnField, onClose, onSave }: SaveFieldModalProps) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const md = getModalTheme(isDark);
  const inputClass = `${ap.input} rounded-md shadow-sm`;
  const selectClass = `${ap.select} rounded-md shadow-sm`;
  const labelClass = `block text-sm font-medium mb-1 ${ap.label}`;

  const [formValues, setFormValues] = useState({
    name: '',
    region: '',
    province: '',
    municipality: '',
    barangay: '',
    total_land_area: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; account?: string }>({});

  /** True until backend centroid lookup finishes (no digisaka.app list cascade on open). */
  const [isResolvingLocation, setIsResolvingLocation] = useState(true);
  const [locationLookupError, setLocationLookupError] = useState('');
  const [resolvedCodes, setResolvedCodes] = useState<DigisakaLocationCodes | null>(null);
  const [manualLocationEdit, setManualLocationEdit] = useState(false);
  const [fullRegionsLoaded, setFullRegionsLoaded] = useState(false);
  // Regions from API
  const [regions, setRegions] = useState<Array<{ region_code: string; region_description: string }>>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  
  // Available provinces based on selected region (stored with name and code)
  const [availableProvinces, setAvailableProvinces] = useState<Array<{ province_name: string; province_code: string }>>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  
  // Cities/municipalities state (Digisaka fetch-cities → municipality)
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  
  // Barangays state for Barangay dropdown
  const [barangays, setBarangays] = useState([]);
  const [isLoadingBarangays, setIsLoadingBarangays] = useState(false);
  /** 1-based block numbers whose barangay/municipality differs from the majority. */
  const [outlierBlockNumbers, setOutlierBlockNumbers] = useState<number[]>([]);
  /** How many block centroids were batch-checked via GEE. */
  const [blocksCheckedCount, setBlocksCheckedCount] = useState(0);
  const [adminBlocks, setAdminBlocks] = useState<BlockAdminSummary[]>([]);
  const [adminGroups, setAdminGroups] = useState<BlockAdminGroup[]>([]);
  const [differLevelsLabel, setDifferLevelsLabel] = useState('');
  /** Active tab when saving mixed-location blocks as separate fields. */
  const [separateTabKey, setSeparateTabKey] = useState('');
  /** Per-group farm names keyed by locationKey. */
  const [separateNames, setSeparateNames] = useState<Record<string, string>>({});
  /** Per-group Digisaka location codes for separate saves. */
  const [groupCodes, setGroupCodes] = useState<
    Record<string, DigisakaLocationCodes & { regionName?: string; provinceName?: string }>
  >({});

  const isAdminUser =
    user?.is_superuser === true || String(user?.role || '').toLowerCase() === 'admin';
  const isLguUser = String(user?.role || '').toLowerCase() === 'lgu';
  const canAssignFarmer = isAdminUser || isLguUser;
  // Backend resolves Farmer profile id; do not use external_user_id (they differ).
  const ownFarmerId = user?.external_farmer_id || null;
  const [selectedFarmer, setSelectedFarmer] = useState<ExplorerFarmerOption | null>(null);

  const resolveFarmerIdForSave = (): number | string | null => {
    if (canAssignFarmer && selectedFarmer?.farmer_id) {
      return selectedFarmer.farmer_id;
    }
    return ownFarmerId;
  };

  const currentLocationMatch = locationMatchesMonitoringArea(
    {
      province: resolvedCodes?.province || formValues.province,
      municipality: resolvedCodes?.municipality || formValues.municipality,
      barangay: resolvedCodes?.barangay || formValues.barangay,
      provinceName: formValues.province,
      municipalityName: formValues.municipality,
      barangayName: formValues.barangay,
    },
    user?.monitoring_area
  );
  const lguLocationError =
    isLguUser && !isResolvingLocation ? monitoringAreaSaveError(currentLocationMatch) : null;

  /**
   * Autofill from WebGIS/GEE:
   * - Multi-block: one fast reduceRegions for ALL block centroids, then one named
   *   lookup on the majority location (not only the first block).
   * - Single block: one centroid name lookup.
   */
  useEffect(() => {
    if (!drawnField || !normalizeDrawnFieldToFeature(drawnField)) {
      setIsResolvingLocation(false);
      return;
    }

    setLocationLookupError('');
    setResolvedCodes(null);
    setManualLocationEdit(false);
    setFullRegionsLoaded(false);
    setOutlierBlockNumbers([]);
    setBlocksCheckedCount(0);
    setAdminBlocks([]);
    setAdminGroups([]);
    setDifferLevelsLabel('');
    setSeparateTabKey('');
    setSeparateNames({});
    setGroupCodes({});
    setSelectedFarmer(null);

    const areaHa = computeDrawnAreaHa(drawnField);
    const feature = normalizeDrawnFieldToFeature(drawnField);
    const rings = geoJsonGeometryToPolygonRings(
      feature?.geometry ?? drawnField?.geometry?.geometry ?? drawnField?.geometry
    );
    const blockPoints = rings
      .map((ring, i) => {
        const c = getPolygonCentroid({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
        });
        if (!c) return null;
        return { id: String(i + 1), longitude: c[0], latitude: c[1] };
      })
      .filter(Boolean) as Array<{ id: string; longitude: number; latitude: number }>;

    const fallbackLonLat =
      getPolygonInteriorLonLat(drawnField) || getPolygonCentroid(drawnField);
    const suffix = Math.floor(1000 + Math.random() * 9000);
    const autoName = `Field ${suffix}`;

    setFormValues((prev) => ({
      ...prev,
      name: autoName,
      region: '',
      province: '',
      municipality: '',
      barangay: '',
      total_land_area: areaHa > 0 ? areaHa.toFixed(2) : '',
    }));

    if (blockPoints.length === 0 && !fallbackLonLat) {
      setLocationLookupError('Could not compute a point inside the polygon.');
      setIsResolvingLocation(false);
      return;
    }

    let cancelled = false;
    setIsResolvingLocation(true);

    (async () => {
      try {
        let nameLon = fallbackLonLat?.[0];
        let nameLat = fallbackLonLat?.[1];

        if (blockPoints.length > 1) {
          // Fast path: all centroids in one GEE filterBounds batch
          const batch = await fetchAdminPcodesForPoints(blockPoints);
          if (cancelled) return;
          const analysis = analyzeBlockAdminLocations(batch.length ? batch : blockPoints.map((p) => ({
            ...p,
            found: false,
          })));
          setBlocksCheckedCount(analysis.checkedCount || blockPoints.length);
          setOutlierBlockNumbers(analysis.outlierBlockNumbers);
          setAdminBlocks(analysis.blocks);
          setAdminGroups(analysis.groups);
          setDifferLevelsLabel(analysis.differLevelsLabel);

          if (analysis.foundCount === 0 && batch.length > 0) {
            setLocationLookupError('Could not resolve admin location for all blocks. Try again.');
          }

          // Enrich each location group with place names + Digisaka codes (parallel, still fast).
          if (analysis.groups.length > 0) {
            const enriched = await Promise.all(
              analysis.groups.map(async (g) => {
                const pt =
                  blockPoints.find((p) => g.pointIds.includes(p.id)) ||
                  blockPoints.find((p) => p.id === String(g.blockNumbers[0]));
                if (!pt) return { group: g, codes: null as DigisakaLocationCodes | null, loc: null };
                const loc = await fetchCentroidLocation(pt.longitude, pt.latitude);
                const batchRow = batch.find((r) => g.pointIds.includes(r.id));
                const codes = loc
                  ? adminPcodesToDigisakaCodes(loc.adminPcodes)
                  : adminPcodesToDigisakaCodes(batchRow?.admin_pcodes);
                const barangayName = loc?.barangay || g.barangay;
                const barangayRow = barangayName
                  ? await resolveDigisakaBarangay(barangayName, codes.municipality)
                  : null;
                if (barangayRow) {
                  codes.barangayId = String(barangayRow.id);
                  if (barangayRow.province_id) codes.province = String(barangayRow.province_id);
                  if (barangayRow.city_id) codes.municipality = String(barangayRow.city_id);
                }
                const named: BlockAdminGroup = {
                  ...g,
                  province: loc?.province || g.province,
                  municipality: loc?.municipality || g.municipality,
                  barangay: barangayName,
                };
                return { group: named, codes, loc, regionName: loc?.region || '' };
              })
            );
            if (cancelled) return;

            const nextGroups = enriched.map((e) => e.group);
            setAdminGroups(nextGroups);
            setAdminBlocks((prev) =>
              prev.map((b) => {
                const g = nextGroups.find((x) => x.locationKey === b.locationKey);
                return g
                  ? {
                      ...b,
                      province: g.province,
                      municipality: g.municipality,
                      barangay: g.barangay,
                    }
                  : b;
              })
            );

            const names: Record<string, string> = {};
            const codesMap: Record<
              string,
              DigisakaLocationCodes & { regionName?: string; provinceName?: string }
            > = {};
            for (const e of enriched) {
              const label = e.group.blockNumbers.map((n) => `b${n}`).join('-');
              names[e.group.locationKey] = `${autoName}-${label}`;
              if (e.codes) {
                codesMap[e.group.locationKey] = {
                  ...e.codes,
                  regionName: e.regionName,
                  provinceName: e.group.province,
                };
              }
            }
            setSeparateNames(names);
            setGroupCodes(codesMap);
            setSeparateTabKey(nextGroups[0]?.locationKey || '');
          }

          const majority = blockPoints.find((p) => p.id === analysis.majorityPointId) || blockPoints[0];
          nameLon = majority.longitude;
          nameLat = majority.latitude;
        } else if (blockPoints.length === 1) {
          setBlocksCheckedCount(1);
          nameLon = blockPoints[0].longitude;
          nameLat = blockPoints[0].latitude;
        }

        if (nameLon == null || nameLat == null) {
          setLocationLookupError('Could not compute a point inside the polygon.');
          return;
        }

        // Named admin levels for the majority (or only) block — one getInfo
        const loc = await fetchCentroidLocation(nameLon, nameLat);
        if (cancelled) return;
        if (!loc) {
          setLocationLookupError('outside_philippines');
          return;
        }

        const {
          regions: regionRows,
          provinces,
          cities: cityRows,
          barangays: brgyRows,
          codes,
        } = centroidToDropdownRows(loc);

        if (cancelled) return;
        setResolvedCodes(codes);
        setRegions(regionRows);
        setAvailableProvinces(provinces);
        setCities(cityRows);
        setBarangays(brgyRows);

        if (loc.barangay && codes.municipality) {
          void resolveDigisakaBarangay(loc.barangay, codes.municipality).then((row) => {
            if (cancelled || !row) return;
            setResolvedCodes((prev) =>
              prev
                ? {
                    ...prev,
                    barangayId: String(row.id),
                    province: String(row.province_id || prev.province),
                    municipality: String(row.city_id || prev.municipality),
                  }
                : prev
            );
            setBarangays([
              {
                id: row.id,
                barangay_name: row.name,
                barangay_code: String(row.id),
              },
            ]);
          });
        }

        setFormValues((prev) => ({
          ...prev,
          region: loc.region,
          province: loc.province,
          municipality: loc.municipality ?? '',
          barangay: loc.barangay ?? '',
        }));
      } catch {
        if (!cancelled) {
          setLocationLookupError('autofill_failed');
        }
      } finally {
        if (!cancelled) {
          setIsResolvingLocation(false);
          setIsLoadingRegions(false);
          setIsLoadingProvinces(false);
          setIsLoadingCities(false);
          setIsLoadingBarangays(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [drawnField]);

  // Load barangays only after the user manually changes municipality (skip on centroid autofill).
  useEffect(() => {
    const fetchBarangays = async () => {
      if (!manualLocationEdit || isResolvingLocation || !formValues.municipality) {
        if (!formValues.municipality) {
          setBarangays([]);
          setFormValues(prev => ({ ...prev, barangay: '' }));
        }
        return;
      }

      const selectedCity = cities.find(city => city.city_name === formValues.municipality);
      if (!selectedCity || !selectedCity.city_code) {
        setBarangays([]);
        return;
      }

      setIsLoadingBarangays(true);
      try {
        const sortedBarangays = await fetchBarangaysListForCityCode(selectedCity.city_code);
        setBarangays(sortedBarangays);
      } catch {
        setBarangays([]);
      } finally {
        setIsLoadingBarangays(false);
      }
    };

    fetchBarangays();
  }, [formValues.municipality, cities, manualLocationEdit, isResolvingLocation]);

  const ensureFullRegionsLoaded = async () => {
    if (fullRegionsLoaded || isLoadingRegions) return;
    setIsLoadingRegions(true);
    try {
      const list = await fetchRegionsListFromApi();
      setRegions(list);
      setFullRegionsLoaded(true);
    } catch {
      /* keep centroid row */
    } finally {
      setIsLoadingRegions(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Reset dependent fields when parent selection changes
    if (name === 'region') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues(prev => ({ ...prev, region: value, province: '', municipality: '', barangay: '' }));
      setCities([]);
      setBarangays([]);
      if (!value) {
        setAvailableProvinces([]);
        return;
      }
      const selectedRegion = regions.find((r) => r.region_description === value);
      if (!selectedRegion?.region_code) {
        setAvailableProvinces([]);
        return;
      }
      setIsLoadingProvinces(true);
      void fetchProvincesListForRegionCode(selectedRegion.region_code)
        .then((list) => {
          setAvailableProvinces(list);
        })
        .finally(() => setIsLoadingProvinces(false));
    } else if (name === 'province') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues(prev => ({ ...prev, province: value, municipality: '', barangay: '' }));
      setBarangays([]);
      if (!value) {
        setCities([]);
        return;
      }
      const selectedProvince = availableProvinces.find(
        (p) => p.province_name.toLowerCase() === value.toLowerCase()
      );
      let provinceCode = selectedProvince?.province_code ?? null;
      if (!provinceCode) {
        provinceCode = resolveProvinceCode(value, selectedProvince);
      }
      if (!provinceCode) {
        setCities([]);
        return;
      }
      setIsLoadingCities(true);
      void fetchCitiesListForProvinceCode(provinceCode)
        .then((list) => {
          setCities(list);
        })
        .finally(() => setIsLoadingCities(false));
    } else if (name === 'municipality') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues(prev => ({ ...prev, municipality: value, barangay: '' }));
      setBarangays([]);
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const validateForm = () => {
    const newErrors: { name?: string; account?: string } = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Field name is required';
    }
    if (isLguUser && !selectedFarmer?.farmer_id) {
      newErrors.account = 'Select a farmer in your monitoring area before saving.';
    }
    if (isLguUser && lguLocationError) {
      newErrors.account = lguLocationError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveField = async () => {
    if (outlierBlockNumbers.length > 0) {
      setErrors((prev) => ({
        ...prev,
        account:
          `Cannot save as one field: blocks are in different ${differLevelsLabel || 'barangay or municipality'}. ` +
          `Use the tabs below to save them as separate fields.`,
      }));
      return;
    }
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const selectedRegion = regions.find((r: { region_description: string }) => r.region_description === formValues.region);
      const selectedProvince = availableProvinces.find((p: { province_name: string }) => p.province_name === formValues.province);
      const selectedCity = cities.find((c: { city_name: string }) => (c as { city_name: string }).city_name === formValues.municipality) as { city_code?: string } | undefined;
      const selectedBarangay = barangays.find((b: { barangay_name?: string }) => ((b as { barangay_name?: string }).barangay_name || '').trim() === (formValues.barangay || '').trim()) as { barangay_code?: string; brgy_code?: string; code?: string; psgc_code?: string; id?: string } | undefined;
      const locationCodes = resolveLocationCodesForPayload(
        resolvedCodes,
        selectedRegion,
        selectedProvince,
        selectedCity,
        selectedBarangay,
        formValues
      );

      const rawGeometry = drawnField.geometry;
      const geometryObj = rawGeometry?.geometry ?? rawGeometry;
      const polygons = geoJsonGeometryToPolygonRings(geometryObj);

      const farmEntry: Record<string, unknown> = {
        region: locationCodes.region,
        province: locationCodes.province,
        municipality: locationCodes.municipality,
        barangay: locationCodes.barangayId || locationCodes.barangay,
        barangay_name: formValues.barangay,
        name: formValues.name.trim(),
        polygons,
      };
      const farmerId = resolveFarmerIdForSave();
      if (farmerId) {
        farmEntry.farmer_id = Number(farmerId) || farmerId;
      }
      if (formValues.total_land_area) {
        farmEntry.total_land_area = parseFloat(formValues.total_land_area);
      }

      // digisaka.app batch create — one farm id may hold multiple polygon rings (block)
      const data = await createFarmsViaDigisakaApi({ farms: [farmEntry] });
      onSave(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message) {
        alert(`Failed to save field: ${message}`);
      } else {
        alert('Failed to save field. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSeparateFields = async () => {
    if (adminGroups.length < 2) return;
    if (isLguUser && !selectedFarmer?.farmer_id) {
      setErrors((prev) => ({
        ...prev,
        account: 'Select a farmer in your monitoring area before saving.',
      }));
      return;
    }
    if (isLguUser) {
      const outside = adminGroups.find((g) => {
        const codes = groupCodes[g.locationKey];
        return !locationMatchesMonitoringArea(codes, user?.monitoring_area).ok;
      });
      if (outside) {
        setErrors((prev) => ({
          ...prev,
          account:
            monitoringAreaSaveError(
              locationMatchesMonitoringArea(groupCodes[outside.locationKey], user?.monitoring_area)
            ) || 'One or more locations are outside your monitoring area.',
        }));
        return;
      }
    }
    const missing = adminGroups.filter((g) => !(separateNames[g.locationKey] || '').trim());
    if (missing.length > 0) {
      setErrors((prev) => ({
        ...prev,
        account: 'Enter a field name for each location tab before saving.',
      }));
      return;
    }

    setIsLoading(true);
    setErrors((prev) => ({ ...prev, account: undefined }));
    try {
      const rawGeometry = drawnField.geometry;
      const geometryObj = rawGeometry?.geometry ?? rawGeometry;
      const allRings = geoJsonGeometryToPolygonRings(geometryObj);

      const farms: Record<string, unknown>[] = [];
      for (const g of adminGroups) {
        const polygons = g.blockNumbers
          .map((n) => allRings[n - 1])
          .filter((ring): ring is number[][] => Array.isArray(ring) && ring.length > 0);
        if (!polygons.length) continue;

        const codes = groupCodes[g.locationKey];
        const areaHa = polygons.reduce((sum, ring) => {
          const ha = calculatePolygonAreaHectares({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [ring] },
          });
          return sum + (ha > 0 ? ha : 0);
        }, 0);

        const farmEntry: Record<string, unknown> = {
          region: codes?.region || '',
          province: codes?.province || '',
          municipality: codes?.municipality || '',
          barangay: codes?.barangayId || codes?.barangay || '',
          barangay_name: g.barangay,
          name: (separateNames[g.locationKey] || '').trim(),
          polygons,
        };
        const farmerId = resolveFarmerIdForSave();
        if (farmerId) {
          farmEntry.farmer_id = Number(farmerId) || farmerId;
        }
        if (areaHa > 0) {
          farmEntry.total_land_area = Number(areaHa.toFixed(2));
        }
        farms.push(farmEntry);
      }

      if (!farms.length) {
        throw new Error('No valid block polygons to save.');
      }

      const data = await createFarmsViaDigisakaApi({ farms });
      onSave(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message) {
        alert(`Failed to save fields: ${message}`);
      } else {
        alert('Failed to save fields. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const mixedLocations = outlierBlockNumbers.length > 0 && adminGroups.length > 1;
  const activeSeparateGroup =
    adminGroups.find((g) => g.locationKey === separateTabKey) || adminGroups[0];
  const activeSeparateAreaHa = (() => {
    if (!activeSeparateGroup || !drawnField) return 0;
    const feature = normalizeDrawnFieldToFeature(drawnField);
    const rings = geoJsonGeometryToPolygonRings(
      feature?.geometry ?? drawnField?.geometry?.geometry ?? drawnField?.geometry
    );
    return activeSeparateGroup.blockNumbers.reduce((sum, n) => {
      const ring = rings[n - 1];
      if (!ring) return sum;
      const ha = calculatePolygonAreaHectares({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      return sum + (ha > 0 ? ha : 0);
    }, 0);
  })();
  const allSeparateNamesFilled =
    adminGroups.length > 0 &&
    adminGroups.every((g) => (separateNames[g.locationKey] || '').trim().length > 0);

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-[9999] p-4 ${md.overlay}`}
      style={{ isolation: 'isolate' }}
    >
      <div
        className={`save-field-modal max-w-md w-full max-h-[90vh] flex flex-col relative ${md.dialog}`}
        style={{ zIndex: 10000, colorScheme: isDark ? 'dark' : 'light' }}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${ap.headerBorder}`}>
          <div className="min-w-0 pr-2">
            <h3 className={`text-lg font-semibold truncate ${ap.heading}`}>
              {formValues.name.trim() || 'Save Field'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className={`disabled:opacity-50 shrink-0 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body: farmer first (name auto-fills), then area; admin skeleton while location loads */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-[min(320px,70vh)]">
          {!isResolvingLocation && locationLookupError && (
            <div
              role="alert"
              className={`rounded-lg border px-3 py-2.5 text-xs ${
                isDark
                  ? 'border-amber-600/40 bg-amber-900/30 text-amber-200'
                  : 'border-amber-200/90 bg-amber-50 text-amber-950/90'
              }`}
            >
              <p className="font-semibold mb-0.5">
                {locationLookupError === 'outside_philippines'
                  ? 'Field appears to be outside the Philippines'
                  : 'Could not auto-detect location'}
              </p>
              <p className="leading-relaxed opacity-80">
                {locationLookupError === 'outside_philippines'
                  ? 'If you think this is a mistake, please select the region and province manually using the dropdowns below.'
                  : 'Location autofill failed. Please select the region and province manually using the dropdowns below.'}
              </p>
            </div>
          )}

          {errors.account && (
            <div
              role="alert"
              className={`rounded-lg border px-3 py-2.5 text-xs ${
                isDark
                  ? 'border-red-600/40 bg-red-900/30 text-red-200'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {errors.account}
            </div>
          )}

          {!isResolvingLocation &&
            blocksCheckedCount > 1 &&
            outlierBlockNumbers.length === 0 &&
            !locationLookupError &&
            adminGroups.length > 0 && (
            <p className={`text-[11px] ${ap.faint}`}>
              Checked {blocksCheckedCount} block centroids — same barangay/municipality.
            </p>
          )}

          {canAssignFarmer && !isResolvingLocation && mixedLocations && (
            <FarmerSearchPicker
              source={isLguUser ? 'monitoring' : 'explorer'}
              required={isLguUser}
              selected={selectedFarmer}
              onSelect={(farmer) => {
                setSelectedFarmer(farmer);
                if (farmer) {
                  const autoName = buildFarmerFarmName(farmer);
                  setSeparateNames((prev) => {
                    const next = { ...prev };
                    for (const group of adminGroups) {
                      if (!(next[group.locationKey] || '').trim()) {
                        next[group.locationKey] = autoName;
                      }
                    }
                    return next;
                  });
                }
              }}
              disabled={isLoading}
            />
          )}

          {!isResolvingLocation && mixedLocations && (
            <div
              className={`rounded-[10px] border px-3 py-2.5 text-left text-xs leading-snug ${
                isDark
                  ? 'border-red-500/40 bg-red-900/25 text-red-100'
                  : 'border-red-300 bg-red-50 text-red-950'
              }`}
              role="alert"
            >
              <p className="font-semibold">Cannot save as one field</p>
              <p className="mt-1">
                Blocks are in different{' '}
                <span className="font-semibold">{differLevelsLabel || 'barangay or municipality'}</span>
                .
              </p>
              <ul className={`mt-2 space-y-1.5 ${isDark ? 'text-red-100/95' : 'text-red-950/95'}`}>
                {adminBlocks.map((b) => (
                  <li key={b.id} className="flex gap-2">
                    <span className="shrink-0 font-semibold">Block {b.blockNumber}</span>
                    <span>
                      {b.municipality}
                      {b.barangay && b.barangay !== '—' ? ` · ${b.barangay}` : ''}
                      {b.province && b.province !== '—' ? ` (${b.province})` : ''}
                      {b.isOutlier ? (
                        <span className={`ml-1 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                          — different {adminGroups.find((g) => g.locationKey === b.locationKey)?.differsIn.join(' & ') || 'location'}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
              <p className={`mt-2 ${isDark ? 'text-red-100/80' : 'text-red-900/80'}`}>
                Name each location below and save as separate fields, or cancel and pick blocks again
                from AI delineation.
              </p>
            </div>
          )}

          {!isResolvingLocation && mixedLocations ? (
            <div className="space-y-3">
              <div
                className={`flex gap-1 overflow-x-auto rounded-[10px] border p-1 ${
                  isDark ? 'border-gray-600 bg-gray-800/60' : 'border-gray-200 bg-gray-50'
                }`}
                role="tablist"
                aria-label="Save blocks by location"
              >
                {adminGroups.map((g, i) => {
                  const active = (separateTabKey || adminGroups[0]?.locationKey) === g.locationKey;
                  return (
                    <button
                      key={g.locationKey}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={isLoading}
                      onClick={() => setSeparateTabKey(g.locationKey)}
                      className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        active
                          ? isDark
                            ? 'bg-emerald-700 text-white'
                            : 'bg-emerald-600 text-white'
                          : isDark
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-white'
                      }`}
                    >
                      Field {i + 1}
                      <span className="opacity-80">
                        {' '}
                        · Block{g.blockNumbers.length > 1 ? 's' : ''} {g.blockNumbers.join(', ')}
                      </span>
                    </button>
                  );
                })}
              </div>

              {activeSeparateGroup && (
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Field name *</label>
                    <input
                      type="text"
                      value={separateNames[activeSeparateGroup.locationKey] || ''}
                      onChange={(e) =>
                        setSeparateNames((prev) => ({
                          ...prev,
                          [activeSeparateGroup.locationKey]: e.target.value,
                        }))
                      }
                      className={inputClass}
                      placeholder={`Name for blocks ${activeSeparateGroup.blockNumbers.join(', ')}`}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Land area (ha)</label>
                    <input
                      type="text"
                      readOnly
                      value={activeSeparateAreaHa > 0 ? activeSeparateAreaHa.toFixed(2) : '—'}
                      className={`${inputClass} opacity-80`}
                    />
                  </div>
                  <div
                    className={`rounded-[10px] border p-3 text-xs space-y-1 ${md.locationBox}`}
                  >
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${ap.muted}`}>
                      Location for Block{activeSeparateGroup.blockNumbers.length > 1 ? 's' : ''}{' '}
                      {activeSeparateGroup.blockNumbers.join(', ')}
                    </p>
                    <p>
                      <span className={ap.faint}>Province: </span>
                      {activeSeparateGroup.province}
                    </p>
                    <p>
                      <span className={ap.faint}>Municipality: </span>
                      {activeSeparateGroup.municipality}
                    </p>
                    <p>
                      <span className={ap.faint}>Barangay: </span>
                      {activeSeparateGroup.barangay}
                    </p>
                    {activeSeparateGroup.differsIn.length > 0 && (
                      <p className={`pt-1 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                        Differs in {activeSeparateGroup.differsIn.join(' and ')} from the other
                        location group.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : !isResolvingLocation ? (
            <>
          {canAssignFarmer && (
            <FarmerSearchPicker
              source={isLguUser ? 'monitoring' : 'explorer'}
              required={isLguUser}
              selected={selectedFarmer}
              onSelect={(farmer) => {
                setSelectedFarmer(farmer);
                if (farmer) {
                  setFormValues((prev) => ({
                    ...prev,
                    name: buildFarmerFarmName(farmer),
                  }));
                }
              }}
              disabled={isLoading}
              error={isLguUser && errors.account && !selectedFarmer ? errors.account : undefined}
            />
          )}

          {isLguUser && lguLocationError && (
            <div
              role="alert"
              className={`rounded-lg border px-3 py-2.5 text-xs ${
                isDark
                  ? 'border-red-600/40 bg-red-900/30 text-red-200'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {lguLocationError}
            </div>
          )}

          {/* Field Name */}
          <div>
            <label className={labelClass}>
              Field Name *
            </label>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              className={`${inputClass} ${errors.name ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : ''}`}
              placeholder={
                canAssignFarmer
                  ? 'Select a farmer to auto-name, or type a name'
                  : 'Enter field name'
              }
              disabled={isLoading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              Total land area (ha)
            </label>
            <input
              type="number"
              name="total_land_area"
              value={formValues.total_land_area}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className={inputClass}
              placeholder="e.g. 5.0"
              disabled={isLoading}
            />
          </div>
            </>
          ) : null}

          {/* Administrative hierarchy: skeleton card while loading, then real fields */}
          {isResolvingLocation ? (
            <div
              className={`rounded-[10px] border p-4 ${md.locationBox}`}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${ap.muted}`}>
                Administrative boundaries
              </p>
              <p className={`mb-3 mt-1 text-xs ${ap.faint}`}>
                Checking block locations…
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(['Region', 'Province', 'Municipality', 'Barangay'] as const).map((label) => (
                  <div key={label} className="space-y-2">
                    <div className={`relative h-3 w-20 overflow-hidden rounded ${isDark ? 'bg-gray-700/90' : 'bg-gray-200/90'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-r from-transparent to-transparent animate-shimmer-slide ${isDark ? 'via-white/10' : 'via-white/70'}`} />
                    </div>
                    <div className={`relative h-10 w-full overflow-hidden rounded-md ${isDark ? 'bg-gray-700/90' : 'bg-gray-200/90'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-r from-transparent to-transparent animate-shimmer-slide ${isDark ? 'via-white/15' : 'via-white/80'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !mixedLocations ? (
            <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>
                Region
              </label>
              <select
                name="region"
                value={formValues.region}
                onChange={handleInputChange}
                onFocus={() => { void ensureFullRegionsLoaded(); }}
                className={selectClass}
                disabled={isLoading || isLoadingRegions}
              >
                <option value="">
                  {isLoadingRegions ? 'Loading regions...' : 'Select Region'}
                </option>
                {regions.map((region) => (
                  <option key={region.region_code} value={region.region_description}>
                    {region.region_description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Province
              </label>
              {availableProvinces.length > 0 ? (
                <select
                  name="province"
                  value={formValues.province}
                  onChange={handleInputChange}
                  className={selectClass}
                  disabled={isLoading || isLoadingProvinces || !formValues.region}
                >
                  <option value="">
                    {isLoadingProvinces ? 'Loading provinces...' : 'Select Province'}
                  </option>
                  {availableProvinces.map((province) => (
                    <option key={province.province_code || province.province_name} value={province.province_name}>
                      {province.province_name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="province"
                  value={formValues.province}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Enter province"
                  disabled={isLoading || isLoadingProvinces || !formValues.region}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>
                Municipality
              </label>
              <select
                name="municipality"
                value={formValues.municipality}
                onChange={handleInputChange}
                className={selectClass}
                disabled={isLoading || isLoadingCities || !formValues.province}
              >
                <option value="">
                  {isLoadingCities
                    ? 'Loading municipalities...'
                    : cities.length === 0 && formValues.province
                      ? 'No municipalities available'
                      : 'Select Municipality'}
                </option>
                {cities.map((city) => (
                  <option key={city.city_code} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Barangay {barangays.length > 0 && <span className={`text-xs ${ap.faint}`}>({barangays.length} available)</span>}
              </label>
              {isLoadingBarangays ? (
                <div className={`w-full px-3 py-2 border rounded-md text-sm ${ap.input} ${ap.muted}`}>
                  Loading barangays... (This may take a moment for large datasets)
                </div>
              ) : barangays.length > 0 ? (
                <div className="space-y-1">
                  <input
                    type="text"
                    list="barangay-list"
                    name="barangay"
                    value={formValues.barangay}
                    onChange={handleInputChange}
                    onInput={(e) => {
                      const value = (e.target as HTMLInputElement).value;
                      setFormValues(prev => ({ ...prev, barangay: value }));
                    }}
                    placeholder={barangays.length > 1000 ? "Type to search barangay..." : "Select or type barangay..."}
                    className={inputClass}
                    disabled={isLoading || !formValues.municipality}
                    autoComplete="off"
                  />
                  <datalist id="barangay-list">
                    {/* Limit datalist options for performance - browser will handle search/filter */}
                    {barangays
                      .slice(0, Math.min(barangays.length, 5000)) // Limit to 5000 for performance
                      .map((barangay) => (
                        <option key={barangay.id} value={barangay.barangay_name} />
                      ))}
                  </datalist>
                  {barangays.length > 1000 && (
                    <p className={`text-xs ${ap.faint}`}>
                      Large dataset detected. Type to search among {barangays.length.toLocaleString()} barangays.
                    </p>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="barangay"
                  value={formValues.barangay}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Enter barangay manually"
                  disabled={isLoading || !formValues.municipality}
                />
              )}
            </div>
          </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${ap.headerBorder}`}>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${md.cancelBtn}`}
          >
            Cancel
          </button>
          {mixedLocations ? (
            <button
              onClick={handleSaveSeparateFields}
              disabled={
                isLoading ||
                isResolvingLocation ||
                !allSeparateNamesFilled ||
                (isLguUser && (!selectedFarmer || Boolean(lguLocationError)))
              }
              className={`px-4 py-2 text-sm font-medium rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${ap.primaryBtn}`}
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>
                {isLoading
                  ? 'Saving...'
                  : `Save ${adminGroups.length} separate fields`}
              </span>
            </button>
          ) : (
            <button
              onClick={handleSaveField}
              disabled={
                isLoading ||
                isResolvingLocation ||
                !formValues.name.trim() ||
                (isLguUser && (!selectedFarmer || Boolean(lguLocationError)))
              }
              className={`px-4 py-2 text-sm font-medium rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${ap.primaryBtn}`}
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isLoading ? 'Saving...' : 'Save Field'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}